import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { VideoPlayer, VideoPlayerEvent, isHlsStream } from "@telus/opus-component-library/video-player";
import {
  formatSecondsToTimeDisplay,
  isFullScreen,
  isPPVEventPurchasable,
  hasADAudioTrack,
} from "../../shared/utils/playerUtils";
import PlayerConstants from "../../shared/constants/player";
import Document from "../../shared/document";
import { getLocalStorage } from "../../shared/utils/localStorage";
import { getSessionStorage, removeSessionStorage } from "../../shared/utils/sessionStorage";
import { getRecordingCTAs } from "../../shared/utils/recordingHelper";
import { handleImageError, getAVSPosterArtImage } from "../../shared/utils/image";
import { useDispatch } from "react-redux";
import { showPlaybackError } from "../../App/state/actions";
import useOutsideClickIndicator from "../../shared/hooks/useOutsideClickIndicator";
import useAppLanguage from "../../shared/hooks/useAppLanguage";
import {
  IsSafari,
  getAutoGeneratedObject,
  getAutogeneratedEpisodeString,
  mapPlayerEvent,
} from "../../shared/utils/index";
import { PlayerEvent } from "../../shared/constants/analytics";
import ProgressBar from "../ProgressBar";
import constants from "../../shared/constants";
import PlaybackError from "../PlaybackError";
import ToastNotification from "../ToastNotification";

import {
  trackMediaBufferComplete,
  trackMediaBufferStart,
  trackMediaComplete,
  trackMediaPause,
  trackMediaSeek,
  trackMediaSeeked,
  trackPlayheadChange,
} from "../../shared/analytics/media";
import { trackGenericAction } from "../../shared/analytics/dataLayer";
import { ANALYTICS_EVENT_TYPES, LINK_INFO } from "../../shared/constants/analytics";
import telusConvivaAnalytics from "../../shared/utils/convivaAnalytics";
import useSetInterval from "../../shared/hooks/useSetInterval";
import { isTimestampFromPastDay } from "../../shared/utils/playbackHelpers";
import storageConstants from "../../shared/constants/storage";
import { getPauseLiveProperties, getFormattedPrice, getPackagePrice } from "../../shared/utils/feedHelper";
import { checkIsStartOverSupported } from "../../shared/utils/epg";

const { LIVE } = PlayerConstants.PLAYER_TYPE;
const { PLAYER_CONTROLS, PLAYER_LIFECYCLE_ERROR_CODES, NO_BROWSER_SUPPORT_ERROR_CODES } = PlayerConstants;
const { SETTINGS_CONFIG, PAGE_CONTENT_ITEM_TYPES } = constants;

const isSafari = IsSafari();
const defaultChannelLogoIcon = process.env.PUBLIC_URL + "/images/default-channel-logo.png";
const { IS_FRESH_LOGIN, PLAYER_LOADED, VIDEO_AUTH_TOKEN } = storageConstants;

import errorConstants from "../../shared/constants/error";
const { AVS_ERROR_CODES } = errorConstants;

/** import modules */
import { usePlayerState } from "./core/playerState";
import { usePlayerVars, usePlayerClassNames } from "./core/playerPropVars";
import { useRecordingInfo } from "./core/recording";
import {
  usePlayerActionUtils,
  isLiveRestartButtonVisible,
  handleRestartedJumpToLive,
  showPurchaseModal,
  getCurrentTimestamp,
  getLiveBufferPercentage,
  updateLiveProgramAiringStatus,
  initLiveProgressStyle,
  calculateNonPltvStreamProgress,
  calculateVodPos,
  handleOnDemandProgressFastForward,
  handleOnDemandProgressRewind,
  scheduleRetry,
  handleRetry,
  shouldRetryByStream,
} from "./core/playerUtils";
import {
  playerExists,
  playerGetDuration,
  shakaGetCurrentTime,
  playerGetCurrentPlaybackTime,
  playerCheckDynamicAsset,
  playerHandleLoadPlaybackSuccess,
  playerIsPlaying,
  playerHandleADToggleChange,
  shakaBuildPlayerSourceConfig,
} from "./playerHelpers/playerCoreUtils";
import {
  playerStartOverStreamHelper,
  isLookbackAssetSeeking,
  lookbackSeekableEndPos,
  playerRestartedStreamProgress,
  playerUpdateLiveRestartProgress,
  playerUpdateLookBackProgress,
  getContentStartSeekTime,
  isVxOA9RestartStream,
  restartPercentToTimestamp,
  playerVxOA9RestartSeek,
} from "./playerHelpers/playerSocuUtils";
import usePlayerEventHandlers from "./playerHelpers/playerEventHandlers";
import { shakaHandleLicenseRequest, shakaHandleManifestResponse } from "./playerHelpers/shakaNetworkConfig";
import {
  playerCheckIsNotAtLivePosition,
  playerHandleDynamicSeeking,
  playerHandleJumpToLive,
  playerIsLive,
  playerLiveStreamProgress,
  shakaHlsLiveProgress,
  playerToggleLiveProgramUpdate,
  playerHandleLiveProgressEnd,
  playerCalculateLandTime,
  playerUpdateLiveProgress,
  playerOnNextLiveProgramStarted,
  shakaHlsLiveTsToSeekPoint,
} from "./playerHelpers/playerLiveUtils";
import { playerGetPlayedTimeForAnalytics } from "./playerHelpers/playerAnalytics";
import {
  playerHandleStaticSeeking,
  playerGetSeekParameters,
  playerCalculateSeekPosition,
  playerSeekableEndPos,
} from "./playerHelpers/playerVodUtils";
import {
  shakaInitPlayerVolume,
  shakaHandlePlayPauseButton,
  shakaSetVolume,
  shakaSetVideoPlayerControlState,
} from "./playerHelpers/shakaPlayerControls";
import {
  usePlayerMountEffect,
  useReloadAppEffect,
  useContentIdEffect,
  usePCBlockEffect,
  useFocusOnSideNavOpen,
  useStillWatchingPromptEffect,
  useCheckPauseLiveAllowed,
} from "./core/playerCoreEffects";
import {
  handleVideoControlsMouseMove,
  handleClickToggleState,
  handleVideoControlsKeyUp,
  handleVolumeKeyUp,
  handleRewindButton,
  handleRestartedStreamForwardButton,
  handleForwardButton,
  handleVolumeButtonHover,
  handleVolumeSlideChange,
  toggleSettingVisibility,
  handleVolumeToggle,
  handleClosePlayer,
  handleFullScreenToggle,
} from "./core/playerControls";
import { usePlayerAnalytics } from "./core/analytics";
import {
  useLivePlayerCoreEffect,
  useLivePlaybackEffect,
  useLiveProgressEffect,
  useJumpToLiveEffect,
} from "./effectModules/playerLiveEffects";
import { usePlayerUnmount, useUnloadPlayer, useInitPlayerControls } from "./effectModules/playerCoreEffects";
import {
  useSOCUInTransition,
  useSOCUTransationComplete,
  useCheckSocuSupportEffect,
  useExitLookbackModeEffect,
} from "./core/socu";
import { usePlaybackErrorEffect, handlePanicLiveSeeking } from "./core/errorHandling";
import PlayerWrapper from "./components/PlayerWrapper";
import ShakaPlayerContainer from "./components/ShakaPlayerContainer";
import RecordingCTA from "./components/RecordingCTA";
import BackgroundImage from "./components/BackgroundImage";
import GradientBackground from "./components/GradientBackground";
import ClosedCaption from "./components/ClosedCaption";
import StillWatchingCloseIcon from "./components/StillWatchingCloseIcon";
import FullScreenToggle from "./components/FullScreenToggle";
import SidebarToggle from "./components/SidebarToggle";
import CloseButton from "./components/CloseButton";
import ChannelInfo from "./components/ChannelInfo";
import Details from "./components/Details";
import VolumeControl from "./components/VolumeControl";
import SettingsControl from "./components/SettingsControl";
import RewindButton from "./components/RewindButton";
import PlayPauseButton from "./components/PlayPauseButton";
import ForwardButton from "./components/ForwardButton";
import RestartButton from "./components/RestartButton";
import JumpToLiveButton from "./components/JumpToLiveButton";
import RecordingButton from "./components/RecordingButton";
import PurchaseButton from "./components/PurchaseButton";
import StillWatchingCard from "./components/StillWatchingCard";
import "./style.scss";
import { Timeout } from "../../shared/constants/conviva";

/**
 * Video player that streams AMC content for VOD and Live streams. Works with AMC's ericsson DRM
 * @component
 * @param {object} props - StreamInfo, MetadataInfo, style and type ( VOD or Live )
 */
function LivePlayer(props) {
  const {
    className,
    type,
    currentContentId,
    playbackType,
    recordingButtonClickHandler,
    isMR,
    onPlaybackStateChange,
    control,
    liveProgramDetails,
    onFullScreenClick,
    showListButton,
    handleListIconClick,
    isSideNavOpen,
    isReadyCallback,
    setIsReadyToChangeStreams,
    showStillWatchingPrompt,
    restartUserInactivityTimer,
    updateVodProgress,
    streamInfo,
    hideListChannel,
    getMediaNodeMetaData = () => null,
    triggerAnalyticsCalls = () => {},
    contentType,
    contentLockedOverlay = null,
    parentalPin = null,
    bookmark,
    livePlaybackCanStart,
    liveProgramCanUpdate,
    triggerRestart,
    isRestartedStream,
    switchLive,
    isReadyToChangeStreams,
    transitionProgram,
    isNextProgramNonRestartable,
    isLookbackStream,
    resetPlayerPage,
    exitLookbackMode,
    changeLookbackStream,
    ppvPurchasePackage,
    purchaseCallback,
    updateSeasonEntitlements,
    initPlaybackTimestamp,
    dtContentType,
    convivaContentSubType,
    isPlayerControlsDisable,
    PCBlocked,
    fetchNextLiveStream,
    triggerLookback,
    exitLookback,
    updatePrevLiveProgramDetails,
    prevLiveProgramDetails,
    currentPlayingSource,
    setTriggerSafariUpdate,
    haltTransitionSOCU,
    isUnrestrictedPlay,
    errCode,
  } = props;
  const {
    appProvider,
    isInHome,
    toastData,
    playbackError,
    spinningLoaderParams,
    reloadAppFlag,
    featureToggles,
    recordingsList,
    manipulatedRecordingParams,
  } = usePlayerState();
  const { assetId, title, subTitle, image, channelNumber, bgImage, extendedMetadata, recordingDeltaTime } =
    usePlayerVars(props);
  const {
    toggleSpinningLoader,
    displayToastNotification,
    displayModalPopup,
    handleStopContent,
    managePlaybackDeltaTimer,
  } = usePlayerActionUtils();
  const {
    playerInitFFStatus,
    playerInitRWStatus,
    playerCheckStartOverStatus,
    handleRetryAndPlayerError,
    shakaInitPlaybackSettings,
    shakaInitADSetting,
    shakaInitCCSetting,
    playerClearLiveAutoPlayTimer,
    shakaSetLiveAutoPlayTimer,
    playerHandleStartOverTransition,
    shakaSetVodProgress,
    playerHandleProgressUpdate,
    playerHandleSocuLiveTransition,
    shakaHandleQualityChange,
    shakaHandleSubtitleChange,
  } = usePlayerEventHandlers();
  const isFreshLogin = getLocalStorage(IS_FRESH_LOGIN);
  const playerLoaded = getSessionStorage(PLAYER_LOADED);
  const initialVolume = getLocalStorage(PLAYER_CONTROLS.VOLUME);
  const isPlayerMute = getLocalStorage(PLAYER_CONTROLS.MUTE) === "true" ? true : false;
  const closeCaptionSetting = getLocalStorage(SETTINGS_CONFIG.CLOSED_CAPTION);
  const dvSetting = getLocalStorage(SETTINGS_CONFIG.DESCRIBED_VIDEO);
  const muteSetting = getLocalStorage(SETTINGS_CONFIG.MUTE);
  const {
    isPauseLiveTVEnabled,
    isRestartLiveTVEnabled,
    isLookbackEnabled,
    isRecordingEnabled,
    isConvivaAppTrackerEnabled,
    isPlaybackRetry,
    isLiveZoetropeEnabled,
    isDynatraceTrackingEnabled,
    isTeamRecordingsEnabled,
  } = featureToggles;

  const history = useHistory();
  const dispatch = useDispatch();
  const { appLanguage } = useAppLanguage();

  const [settingsDropdownVisibility, setSettingsDropdownVisibility] = useState(false);
  const [isMuted, setIsMuted] = useState(isPlayerMute);
  const [volumeLevel, setVolumeLevel] = useState(initialVolume ? initialVolume : 0);
  const [volumeVisibility, setVolumeVisibility] = useState(false);
  const [videoControlsStyle, setVideoControlsStyle] = useState({});
  const [playPauseIcon, setPlayPauseIcon] = useState("/images/playback-pause.svg");
  const [progressStyle, setProgressStyle] = useState({});
  const [elapsedTime, setElapsedTime] = useState("");
  const [remainingTime, setRemainingTime] = useState(null);
  const [showNextEpisodeCard, setShowNextEpisodeCard] = useState(false);
  const [startTime, setStartTime] = useState(props.metadataInfo?.startTime?.programStartTime);
  const [endTime, setEndTime] = useState(
    props.metadataInfo?.startTime?.programStartTime + props.metadataInfo?.duration * 1000
  );
  const [isADAvailable, setADAvailable] = useState(false);
  const [isFFEnabled, setFFEnabled] = useState(false);
  const [isRWEnabled, setRWEnabled] = useState(false);
  const [ffErrorStatus, setFFErrorStatus] = useState(true);
  const [showBgImage, setShowBgImage] = useState(true);
  const [ccList, setCCList] = useState([]);
  const [isCCAvailable, setIsCCAvailable] = useState(false);
  const [userHasClick, setUserHasClick] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoSource, setVideoSource] = useState(null);
  const [currentImageTrack, setCurrentImageTrack] = useState(null);
  const [isSourceLoaded, setIsSourceLoaded] = useState(false);
  const [programCurrentlyAiring, setProgramCurrentlyAiring] = useState(isLookbackStream ? false : true);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const playerMgr = useRef(null);
  const isClosedCaptionEnabled = useRef(closeCaptionSetting ?? false);
  const isDescribedVideoEnabled = useRef(dvSetting ?? false);
  const ccRef = useRef([]);
  const volumeRef = useRef(null);
  const settingsRef = useRef(null);
  const videoCCContainerRef = useRef(null);
  const videoWrapper = useRef(null);
  const videoControlsTimer = useRef(null);
  const autoPlayTimer = useRef(null);
  const stillWatchingCard = useRef(null);
  const playButtonRef = useRef(null);
  const volumeButtonRef = useRef(null);
  const volumeBarRef = useRef(null);
  const bookmarkTimer = useRef(null);
  const { t: translate } = useTranslation();
  const playbackDeltaTimer = useRef(null);
  const playbackTimeDelta = useRef(0); // how many seconds of actual playback has occurred in a player session
  const activeContentId = useRef(currentContentId);
  const hasCalledStopContent = useRef(false);
  const landTime = useRef(null);
  const leftMargin = useRef(null);
  const showLiveProgress = useRef(false);
  const autoPlayLiveTimer = useRef(null);
  const hasJumpedToLive = useRef(false);
  const hasNextProgramStarted = useRef(false); // In order to capture PLTV program change event
  const prevEndTime = useRef(null); // Captures endTime state before update
  const prevLiveBufferProgressPercentage = useRef(0); // Locks liveBufferProgressPercentage when current program is ended and playback is paused in current program
  const isPauseLiveAllowed = useRef(false); // boolean ref to act as a check for pltv
  const isStartOverAllowed = useRef(false); // boolean ref to act as a check for restart
  const isCatchUpAllowed = useRef(false); // boolean ref to act as check for lookback
  const isRestartTrickPlayAllowed = useRef(false); // boolean ref to act as a check for restart and lookback trickplay (specifically ability to skip forward)
  const isClosingPlayer = useRef(false); // boolean ref to act as a check if user has opted to close the video player, need this to avoid unnecessary stream transitions
  const startingOverStream = useRef(false); // boolean ref to act as a check if restarting of a live stream is in progress
  const isTimeShifting = useRef(false); // boolean ref to track if a live stream or a start over stream timeshift is in progress
  const isSeeking = useRef(false); // boolean ref to track if a stream seek is in progress
  const blockPlayback = useRef(false);
  const streamPlaybackType = useRef(null); // ref to store the playback type of the loaded stream
  const cancelTokenSource = useRef(axios.CancelToken.source()); // cancelTokenSource ref for requests unmount clean up
  const bookmarkCreated = useRef(bookmark);
  const activeExtendedMetadata = useRef(extendedMetadata);
  const retryTimer = useRef(null);
  const retryTsValue = useRef();
  const isNextLiveStreamFetched = useRef(false); // ref to track if the details of next live stream in queue are fetched
  const nextLiveProgramDetails = useRef(null); // ref to preserve next live stream details across re-renders
  const liveStreamTransitionInProgress = useRef(false); // ref to indicate if a live stream transition is in progress
  const videoProgressMade = useRef(false);
  const currentManifestUrl = useRef(null);
  const shakaIsPlaybackStarted = useRef(false);
  const retryInPlace = useRef(false);
  const shakaKeyRotationToken = useRef(null);
  const shakaKeyRotationExpiryTimeout = useRef(null);
  const shakaHlsLandTimeSeekHead = useRef(null);
  const restartProgramStartTimeRef = useRef(null);
  const featureTogglesRef = useRef(featureToggles);
  featureTogglesRef.current = featureToggles;

  const spinnerToggle = spinningLoaderParams?.toggle;
  const { playerLoaderClassName, videoClassName, videoControlsName } = usePlayerClassNames(className);

  /* variable for pause live properties. */
  const pauseLiveProperties = isPauseLiveTVEnabled && getPauseLiveProperties(appProvider);

  const [showPurchaseCTA, setShowPurchaseCTA] = useState(
    isPPVEventPurchasable(appProvider, ppvPurchasePackage, endTime)
  );

  const intervalTicker = useSetInterval(1000);
  const isPlayheadAvailable = isFFEnabled && isRWEnabled;
  const cdnUrl = useRef(null);
  const getPlayheadCallback = useCallback(() => {
    return playerGetPlayedTimeForAnalytics(isPlayheadAvailable, playerMgr);
  }, [isPlayheadAvailable]);

  // Const used to determine true/false if content type is fast channel
  const isFast = liveProgramDetails?.channel?.metadata?.extendedMetadata?.isFast === "Y" || false;

  // Const used to determine true/false if content type is LIVE and time shift is allowed
  const checkIfPauseLiveAllowed = useCallback(() => {
    return type === LIVE && isPauseLiveAllowed.current;
  }, [type]);

  // Const used to determine true/false if content type is LIVE and start over is allowed
  const checkIfStartOverAllowed = () => {
    return isRestartLiveTVEnabled ? type === LIVE && isStartOverAllowed.current : false;
  };

  // Const used to determine true/false if content type is LIVE and catch up is allowed
  const checkIfCatchUpAllowed = () => {
    return type === LIVE && isLookbackStream && isCatchUpAllowed.current;
  };

  const isDynamicAsset = () => {
    return playerCheckDynamicAsset(playerMgr);
  };

  /* const used to check if the current playback time of the live stream differs from the on now live position. */
  const isNotAtLivePosition = useCallback(() => {
    return playerCheckIsNotAtLivePosition(playerMgr, isRestartedStream, isHlsStream(currentManifestUrl.current));
  }, [isRestartedStream]);

  /**
   * Function to make the Stop Content call for AVS backend. If current content is VOD,
   * save bookmark info too.
   */
  const handleAVSStopContent = useCallback(
    (playerError = false, newContentId = null) => {
      shakaIsPlaybackStarted.current = false;
      if (isUnrestrictedPlay.current) return;
      return handleStopContent({
        appProvider,
        activeContentId,
        hasCalledStopContent,
        playbackType,
        playbackTimeDelta,
        buildBookmarkCallback: null,
        playerLoaderClassName,
        isRestartedStream,
        isLookbackStream,
        liveProgramDetails,
        setIsReadyToChangeStreams,
        exitLookbackMode,
        changeLookbackStream,
        switchLive,
        type,
        updateSeasonEntitlements,
        startingOverStream,
        isClosingPlayer,
        playerError,
        newContentId,
      });
    },
    [
      isUnrestrictedPlay,
      handleStopContent,
      appProvider,
      playbackType,
      playerLoaderClassName,
      isRestartedStream,
      isLookbackStream,
      liveProgramDetails,
      setIsReadyToChangeStreams,
      exitLookbackMode,
      changeLookbackStream,
      switchLive,
      type,
      updateSeasonEntitlements,
    ]
  );

  // function to stop the current dynamic stream and direct player to restart the stream
  const startOverStream = useCallback(
    (transitionLookbackStream = false) => {
      playerStartOverStreamHelper(
        type,
        isLookbackStream,
        transitionLookbackStream,
        playerMgr,
        setIsReadyToChangeStreams,
        handleAVSStopContent,
        startingOverStream,
        triggerRestart,
        liveProgramDetails,
        null
      );
    },
    [handleAVSStopContent, setIsReadyToChangeStreams, triggerRestart, liveProgramDetails, isLookbackStream, type]
  );

  const {
    propsRef,
    trackPlaybackStartTime,
    trackConvivaAppTrackerEvent,
    trackCloseClick,
    closedCaption,
    rwCount,
    ffCount,
    trackMediaStop,
    describedVideo,
  } = usePlayerAnalytics(
    props,
    convivaContentSubType,
    triggerAnalyticsCalls,
    isRestartedStream,
    bookmark,
    isLookbackStream
  );

  const { isNextProgramNonRestartableRef } = useLivePlayerCoreEffect(
    liveProgramDetails,
    playerMgr,
    propsRef,
    props,
    cdnUrl,
    isNextProgramNonRestartable
  );

  /**
   * Helper function to start or stop the playback delta timer.
   * The playback delta timer is used to track the number of playback seconds that occur
   * in a player session.
   * @param {Boolean} shouldStart
   */
  const controlPlaybackDeltaTimer = (shouldStart) => {
    managePlaybackDeltaTimer(playbackDeltaTimer, playbackTimeDelta, shouldStart);
  };

  const handleNextLiveProgramStart = () => {
    playerOnNextLiveProgramStarted(playerMgr, endTime, hasNextProgramStarted);
  };

  usePlayerMountEffect({
    isOnDemandType: false,
    playbackType,
    setPlayNextProgram: () => {},
    playButtonRef,
    volumeButtonRef,
    playerLoaded,
    isFreshLogin,
    cancelTokenSource,
    bookmarkTimer,
    videoControlsTimer,
    autoPlayTimer,
    controlPlaybackDeltaTimer,
    handleAVSStopContent,
    setCCList,
  });

  useReloadAppEffect(reloadAppFlag, appProvider);

  useSOCUInTransition(prevLiveProgramDetails, liveProgramDetails, liveStreamTransitionInProgress, haltTransitionSOCU);

  useSOCUTransationComplete(
    liveProgramDetails,
    liveStreamTransitionInProgress,
    handleAVSStopContent,
    currentContentId,
    checkIsStartOverSupported,
    isInHome,
    transitionProgram,
    startOverStream,
    setStartTime,
    setEndTime,
    videoWrapper,
    handleNextLiveProgramStart,
    haltTransitionSOCU
  );

  const { recordingInfo, showRecordingCTALoading } = useRecordingInfo(
    type,
    isRecordingEnabled,
    recordingsList,
    liveProgramDetails,
    appProvider,
    isMR,
    manipulatedRecordingParams,
    isTeamRecordingsEnabled
  );

  const handleOutsideVolumeClick = () => {
    setVolumeVisibility(false);
  };
  useOutsideClickIndicator(volumeRef, handleOutsideVolumeClick);

  const handleOutsideSettingsClick = () => {
    setSettingsDropdownVisibility(false);
  };
  useOutsideClickIndicator(settingsRef, handleOutsideSettingsClick);

  useContentIdEffect({
    controlPlaybackDeltaTimer,
    handleAVSStopContent,
    hasCalledStopContent,
    activeContentId,
    currentContentId,
    isSafari,
    getSessionStorage,
    setUserHasClick,
  });

  usePCBlockEffect(PCBlocked, blockPlayback);

  useUnloadPlayer(playerMgr, currentContentId);

  usePlayerUnmount(playerMgr);

  // Player retry for fetching a new JWT and manifest
  const performRetry = async () => {
    await handleRetry(
      appProvider,
      isLookbackStream ? PAGE_CONTENT_ITEM_TYPES.program : contentType,
      currentContentId,
      assetId,
      parentalPin,
      retryTimer,
      toggleSpinningLoader,
      shakaInitializePlayer,
      createRetryTimer
    );
  };

  // Player retry logic base on offset interval
  const createRetryTimer = () => {
    scheduleRetry(retryTimer, retryTsValue, isPlaybackRetry, performRetry);
  };

  const shakaInitializePlayer = (manifest = null, jwtToken = null) => {
    if ((isUnrestrictedPlay.current || isReadyToChangeStreams) && streamInfo) {
      setShowErrorOverlay(false);
      const handleLicenseRequest = (request) => {
        return shakaHandleLicenseRequest(
          request,
          shakaIsPlaybackStarted,
          shakaKeyRotationToken,
          shakaKeyRotationExpiryTimeout,
          contentType,
          appProvider,
          currentContentId,
          assetId,
          parentalPin
        );
      };
      const handleManifestResponse = (response) => {
        return shakaHandleManifestResponse(response, isSafari, false, cdnUrl);
      };
      const manifestUrl = manifest ? manifest : streamInfo.manifestURL;
      currentManifestUrl.current = manifestUrl;
      const authToken = jwtToken ? jwtToken : streamInfo.authToken;
      const source = shakaBuildPlayerSourceConfig(
        manifestUrl,
        authToken,
        streamInfo.contentId,
        streamInfo.LA_URL,
        streamInfo.LA_CERT_URL,
        handleManifestResponse,
        handleLicenseRequest,
        true,
        isFast
      );

      // Report playback origin if it's customer triggered or retry
      telusConvivaAnalytics.reportSessionStart(retryInPlace.current);

      setVideoSource(source);
    }
  };

  useEffect(() => {
    if (!streamInfo || streamInfo === null) return;
    if (errCode?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS)) {
      setShowErrorOverlay(true);
      dispatch(showPlaybackError(errCode, translate("error_try_again_later")));
      return;
    }
    if (
      currentPlayingSource.current === null ||
      currentPlayingSource.current.manifestURL !== streamInfo.manifestURL ||
      (appProvider.ENV !== "avs-prod" &&
        appProvider.ENV !== "avs-beta" &&
        currentPlayingSource.current.manifestURL === streamInfo.manifestURL &&
        currentPlayingSource.current.contentId !== streamInfo.contentId &&
        isReadyToChangeStreams)
    ) {
      currentPlayingSource.current = streamInfo;
    } else {
      return;
    }
    bookmarkCreated.current = bookmark;
    activeExtendedMetadata.current = extendedMetadata;
    streamPlaybackType.current = playbackType;

    toggleSpinningLoader(true, playerLoaderClassName);
    setShowBgImage(true);
    autoPlayTimer.current = null;
    autoPlayLiveTimer.current = null;
    clearTimeout(autoPlayLiveTimer.current);
    setProgressStyle(0);
    if (updateVodProgress) updateVodProgress(0);
    setElapsedTime("");
    setCCList([]);
    ccRef.current = [];
    playbackTimeDelta.current = 0;
    isNextLiveStreamFetched.current = false;
    if (liveProgramDetails) {
      updatePrevLiveProgramDetails(liveProgramDetails, isLookbackStream, isRestartedStream);
    }
    if (isRestartedStream) {
      landTime.current = liveProgramDetails?.metadata?.airingStartTime;
    } else {
      landTime.current = null;
    }
    showLiveProgress.current = false;

    shakaInitializePlayer();

    hasCalledStopContent.current = false;

    if (checkIfPauseLiveAllowed()) {
      setPlayPauseIcon("/images/playback-pause.svg");
    }

    !isLookbackStream && setProgramCurrentlyAiring(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamInfo]);

  useLivePlaybackEffect(contentType, isSourceLoaded, livePlaybackCanStart, playbackError, playerMgr);

  useInitPlayerControls(playerMgr, control);

  useFocusOnSideNavOpen(videoWrapper, isSideNavOpen);

  useStillWatchingPromptEffect(
    showStillWatchingPrompt,
    setShowNextEpisodeCard,
    autoPlayTimer,
    stillWatchingCard,
    setVideoControlsStyle
  );

  useEffect(() => {
    if (intervalTicker > 0 && playerExists()) {
      setShowPurchaseCTA(isPPVEventPurchasable(appProvider, ppvPurchasePackage, endTime));
      trackPlayheadChange(isPlayheadAvailable, getPlayheadCallback);
    }
  }, [appProvider, endTime, getPlayheadCallback, intervalTicker, isPlayheadAvailable, ppvPurchasePackage]);

  const currentPlaybackTime = playerGetCurrentPlaybackTime(playerMgr, currentManifestUrl.current);

  useLiveProgressEffect(
    endTime,
    startTime,
    currentPlaybackTime,
    checkIfPauseLiveAllowed,
    playerMgr,
    hasNextProgramStarted,
    landTime,
    setPlayPauseIcon,
    leftMargin,
    shakaHlsLandTimeSeekHead
  );

  useJumpToLiveEffect(hasJumpedToLive, playerMgr, showLiveProgress, landTime, startTime, hasNextProgramStarted);

  useCheckPauseLiveAllowed(liveProgramDetails, isPauseLiveTVEnabled, isPauseLiveAllowed);

  useCheckSocuSupportEffect(
    liveProgramDetails,
    isRestartLiveTVEnabled,
    isInHome,
    isLookbackEnabled,
    isLookbackStream,
    isCatchUpAllowed,
    isStartOverAllowed,
    isRestartTrickPlayAllowed
  );

  useExitLookbackModeEffect(exitLookbackMode, hasCalledStopContent, handleAVSStopContent);

  usePlaybackErrorEffect(
    playbackError,
    translate,
    getMediaNodeMetaData,
    type,
    isConvivaAppTrackerEnabled,
    activeContentId,
    hasCalledStopContent
  );

  let initPlayerTimestamp = useRef(null);

  /**
   * Shaka player event handlers start
   */
  const handleSourceLoadingEvent = (event) => {
    // Set the initial timestamp for the player init metrics
    initPlayerTimestamp.current = getCurrentTimestamp();
    haltTransitionSOCU.current = false;
  };

  const handleSourceLoadedEvent = async (event) => {
    shakaInitPlaybackSettings(
      type,
      currentContentId,
      assetId,
      videoProgressMade,
      isSafari,
      playerMgr,
      setShowBgImage,
      controlPlaybackDeltaTimer
    );
    // Apply CC settings on source load (not Safari) - don't track as user action
    if (!isSafari) onSubTitleChange(isClosedCaptionEnabled.current, false);
    initLiveProgressStyle(type, showLiveProgress);
    playerClearLiveAutoPlayTimer(autoPlayLiveTimer);
    isReadyCallback && isReadyCallback();
    if (isNextProgramNonRestartableRef.current) {
      displayToastNotification("playback_from_live_point");
    }
    playerHandleLoadPlaybackSuccess(propsRef, props, cdnUrl, playerMgr);
    if (isRestartedStream && !isHlsStream(currentManifestUrl.current)) {
      // Store the restart time to seek in the ready event
      restartProgramStartTimeRef.current = getContentStartSeekTime(playerMgr, startTime / 1000);
    }
    playerMgr.current.play();
    setCurrentImageTrack(playerMgr.current.getImageTracks());
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.SourceLoaded),
      playbackError,
      resetPlayerPage
    );
  };

  const handleReadyEvent = (event) => {
    shakaInitPlayerVolume(playerMgr, volumeLevel, muteSetting, setIsMuted, setVolumeLevel);
    playerInitFFStatus(
      false,
      checkIfCatchUpAllowed(),
      setVideoStarted,
      activeExtendedMetadata,
      setFFEnabled,
      isRestartTrickPlayAllowed,
      setFFErrorStatus,
      isFFEnabled
    );
    playerInitRWStatus(false, activeExtendedMetadata, setRWEnabled, checkIfCatchUpAllowed());
    playerCheckStartOverStatus(isRestartedStream, isLookbackStream, startingOverStream);

    // Perform seek for restarted streams if we have a stored start time
    if (restartProgramStartTimeRef.current !== null) {
      playerMgr.current.seek(restartProgramStartTimeRef.current);
      restartProgramStartTimeRef.current = null; // Reset after use
    }

    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Ready),
      playbackError,
      resetPlayerPage
    );
  };

  const handlePlayingEvent = (event) => {
    if (!shakaIsPlaybackStarted.current) {
      toggleSpinningLoader(false);
      if (isSafari) {
        setTimeout(() => {
          shakaInitADSetting(playerMgr, isDescribedVideoEnabled, setADAvailable, hasADAudioTrack);
          shakaInitCCSetting(playerMgr, setIsCCAvailable, isClosedCaptionEnabled);
        }, [1000]);
      } else {
        shakaInitADSetting(playerMgr, isDescribedVideoEnabled, setADAvailable, hasADAudioTrack);
        shakaInitCCSetting(playerMgr, setIsCCAvailable, isClosedCaptionEnabled);
      }

      if (isHlsStream(currentManifestUrl.current) && isRestartedStream)
        playerMgr.current?.seek(shakaHlsLiveTsToSeekPoint(playerMgr, startTime));
      shakaIsPlaybackStarted.current = true;
      trackPlaybackStartTime(
        initPlaybackTimestamp,
        initPlayerTimestamp,
        dtContentType,
        isDynatraceTrackingEnabled,
        retryInPlace.current
      );
      retryInPlace.current = false;
    }
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Playing),
      playbackError,
      resetPlayerPage
    );
  };

  const handlePausedEvent = (event) => {
    toggleSpinningLoader(false);
    controlPlaybackDeltaTimer(false);
    trackMediaPause();
    shakaSetLiveAutoPlayTimer(
      isPauseLiveAllowed,
      isRestartedStream,
      isLookbackStream,
      pauseLiveProperties,
      autoPlayLiveTimer,
      playerMgr,
      setPlayPauseIcon,
      videoControlsMouseMove
    );
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Paused),
      playbackError,
      resetPlayerPage
    );
  };

  const handlePlaybackFinishedEvent = (event) => {
    playerHandleStartOverTransition(
      isRestartedStream,
      isLookbackStream,
      nextLiveProgramDetails,
      triggerLookback,
      exitLookback,
      liveProgramCanUpdate,
      onPlaybackStateChange
    );
    controlPlaybackDeltaTimer(false);
    removeSessionStorage(PLAYER_LOADED);
    trackMediaComplete();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.PlaybackFinished),
      playbackError,
      resetPlayerPage
    );
  };

  const handleTimeChangedEvent = (event) => {
    if (blockPlayback.current) {
      shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.PAUSE);
    }

    playerHandleSocuLiveTransition(
      event,
      isRestartedStream,
      isLookbackStream,
      liveProgramDetails,
      playerMgr,
      fetchNextLiveStream,
      nextLiveProgramDetails,
      isNextLiveStreamFetched,
      handlePlaybackFinishedEvent,
      setIsReadyToChangeStreams
    );
    playerHandleProgressUpdate(
      event,
      playerMgr,
      isRestartedStream,
      setElapsedTime,
      setRemainingTime,
      isDynamicAsset,
      endTime,
      props,
      setProgressStyle,
      updateVodProgress
    );
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.TimeChanged),
      playbackError,
      resetPlayerPage
    );
    // Unfortunately this is the only way we can prevent CC from showing up when launching HLS.
    // TrackText can't be control until content is playing for HLS.
    // Pass false to NOT track this as a user action (Safari HLS workaround)
    if (isSafari) onSubTitleChange(isClosedCaptionEnabled.current, false);
  };

  const handleCaptionTrackAddedEvent = (event) => {
    if (event.subtitle?.id && isClosedCaptionEnabled.current) {
      closedCaption.current = 1;
    }
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.SubtitleAdded),
      playbackError,
      resetPlayerPage
    );
  };

  const handleSeekingEvent = (event) => {
    controlPlaybackDeltaTimer(false);
    toggleSpinningLoader(true, playerLoaderClassName);
    if (event && event.time) {
      isSeeking.current = true;
      shakaSetVodProgress(
        event.time,
        playerMgr,
        isRestartedStream,
        setElapsedTime,
        setRemainingTime,
        setProgressStyle,
        updateVodProgress
      );
    }
    if (playerIsLive(playerMgr)) isTimeShifting.current = true;
    trackMediaSeek();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Seek),
      playbackError,
      resetPlayerPage
    );
  };

  const handleSeekedEvent = (event) => {
    controlPlaybackDeltaTimer(playerIsPlaying(playerMgr));
    toggleSpinningLoader(false);
    isSeeking.current = false;
    if (playerIsLive(playerMgr)) isTimeShifting.current = false;
    trackMediaSeeked();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Seeked),
      playbackError,
      resetPlayerPage
    );
  };

  const handleBufferingEvent = (event) => {
    toggleSpinningLoader(true, playerLoaderClassName);
    trackMediaBufferStart();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.StallStarted),
      playbackError,
      resetPlayerPage
    );
  };

  const handleBufferedEvent = (event) => {
    toggleSpinningLoader(false);
    trackMediaBufferComplete();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.StallEnded),
      playbackError,
      resetPlayerPage
    );
  };

  const handleVideoQualityChanged = (event) => {
    const shakaStats = playerMgr.current.getFullStats();
    shakaHandleQualityChange(event, playerMgr);
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      {
        type: PlayerEvent.VideoPlaybackQualityChanged,
        targetQuality: { bitrate: shakaStats?.streamBandwidth, height: shakaStats?.height, width: shakaStats?.width },
      },
      playbackError,
      resetPlayerPage
    );
  };

  // Used to determine if retry allowed, specifically playback types can be in panic state
  const isRetryPlayback = () => {
    return shouldRetryByStream(
      true,
      false,
      isFast,
      isRestartedStream,
      false,
      isLookbackStream,
      featureTogglesRef.current
    );
  };

  const handlePlaybackError = (event) => {
    console.error("[Player] player error event = ", event);
    if (!PLAYER_LIFECYCLE_ERROR_CODES.includes(event?.code)) {
      setVideoSource(null);
      shakaIsPlaybackStarted.current = false;
      retryInPlace.current = true;
      handleRetryAndPlayerError(
        isPlaybackRetry,
        videoProgressMade,
        playerLoaderClassName,
        playerMgr,
        createRetryTimer,
        setShowBgImage,
        controlPlaybackDeltaTimer,
        ffErrorStatus,
        event,
        setFFErrorStatus,
        onPlaybackStateChange,
        isRetryPlayback,
        handleAVSStopContent,
        activeContentId.current
      );
      // Due to the limitations we can't perform a pre check for browser compatibility, this is the only way to avoid 4032 from client side
      if (event?.severity === 2 && !NO_BROWSER_SUPPORT_ERROR_CODES.includes(event?.code)) {
        setShowErrorOverlay(true);
        if (!playbackError || Object.values(playbackError).every((value) => value === null)) {
          dispatch(showPlaybackError(event.code, event.message));
        }
        telusConvivaAnalytics.onPlaybackStateChanged(
          propsRef,
          playerMgr.current,
          {
            type: PlayerEvent.Error,
            message: event.message,
            code: event.code,
          },
          playbackError,
          resetPlayerPage
        );
      }
    }
  };

  const handleWarningEvent = (event) => {
    console.warn("[Player] player warning event = ", event);
  };

  // DOM Event Handlers
  const videoControlsMouseMove = (e) => {
    handleVideoControlsMouseMove(
      setVideoControlsStyle,
      videoControlsTimer,
      setVolumeVisibility,
      setSettingsDropdownVisibility,
      videoWrapper
    );
  };
  //Click Events Handler
  const onClickToggleState = (event) => {
    const isTimeShiftAllowed = checkIfPauseLiveAllowed();
    handleClickToggleState(
      event,
      type,
      isTimeShiftAllowed,
      spinnerToggle,
      playPauseButtonHandler,
      onFullScreenButtonHandler,
      signalUserActivity
    );
  };
  const videoControlsKeyUp = (event) => {
    const isTimeShiftAllowed = checkIfPauseLiveAllowed();
    const isStartOverAllowed = checkIfStartOverAllowed();
    handleVideoControlsKeyUp(
      event,
      spinnerToggle,
      showNextEpisodeCard,
      showStillWatchingPrompt,
      videoControlsMouseMove,
      false,
      isTimeShiftAllowed,
      isRWEnabled,
      setVolumeVisibility,
      rewindButtonHandler,
      isFFEnabled,
      isStartOverAllowed,
      isRestartedStream,
      forwardButtonHandler,
      playPauseButtonHandler
    );
  };

  const handleVolume = (event) => {
    handleVolumeKeyUp(
      event,
      showNextEpisodeCard,
      showStillWatchingPrompt,
      videoControlsMouseMove,
      volumeBarRef,
      setVolumeVisibility,
      settingsDropdownVisibility,
      setSettingsDropdownVisibility,
      onFullScreenButtonHandler,
      volumeButtonHandler
    );
  };

  const playPauseButtonHandler = (event) => {
    // Clear the auto play live timer if user presses play
    playerClearLiveAutoPlayTimer(autoPlayLiveTimer);
    shakaHandlePlayPauseButton(playerMgr, shakaSetVideoPlayerControlState, setPlayPauseIcon, signalUserActivity);
  };

  const rewindButtonHandler = (event) => {
    handleRewindButton(videoWrapper, toggleSpinningLoader, playerLoaderClassName, seekPlayback, signalUserActivity);
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.REWIND, getMediaNodeMetaData);
    rwCount.current++;
  };

  const forwardButtonHandler = (event) => {
    if (isRestartedStream || checkIfCatchUpAllowed()) {
      handleRestartedStreamForwardButton(
        videoWrapper,
        isRestartTrickPlayAllowed,
        displayToastNotification,
        toggleSpinningLoader,
        playerLoaderClassName,
        seekPlayback,
        signalUserActivity
      );
    } else {
      handleForwardButton(videoWrapper, toggleSpinningLoader, playerLoaderClassName, seekPlayback, signalUserActivity);
    }
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.FAST_FORWARD, getMediaNodeMetaData);
    ffCount.current++;
  };
  const settingButtonHandler = (event) => {
    toggleSettingVisibility(
      setVolumeVisibility,
      setSettingsDropdownVisibility,
      settingsDropdownVisibility,
      signalUserActivity
    );
  };

  const volumeButtonHandler = (event) => {
    handleVolumeToggle(
      userHasClick,
      setUserHasClick,
      setSettingsDropdownVisibility,
      setVolumeVisibility,
      event,
      isMuted,
      shakaSetVolume,
      playerMgr,
      setVolumeLevel,
      initialVolume,
      setIsMuted,
      signalUserActivity
    );
  };

  const volumeButtonHoverHandler = (e) => {
    handleVolumeButtonHover(setSettingsDropdownVisibility, setVolumeVisibility, volumeBarRef);
  };

  const onVolumeSlideChange = (input) => {
    handleVolumeSlideChange(input, setVolumeLevel, setIsMuted, signalUserActivity);
    shakaSetVolume(playerMgr, input);
  };

  /**
   * A handler which will be triggered when VideoPlyer is closed
   * @param {Object} event event
   * @param {Boolean} isNotStillWatching 'true' when no response occurs after "Still Watching" prompt is ignored
   */
  const closeClickHandler = (event, isNotStillWatching = false) => {
    trackCloseClick(getMediaNodeMetaData, isDynatraceTrackingEnabled);
    trackMediaStop(getMediaNodeMetaData, isDynatraceTrackingEnabled);
    handleClosePlayer(
      isClosingPlayer,
      isFreshLogin,
      playerLoaded,
      isNotStillWatching,
      hasCalledStopContent,
      handleAVSStopContent,
      history
    );
  };

  const onFullScreenButtonHandler = (event) => {
    handleFullScreenToggle(onFullScreenClick, Document, videoWrapper, signalUserActivity);
  };

  /**
   * Set trick play on click
   * @param {*} event
   */
  const setProgress = (progress) => {
    if (checkIfCatchUpAllowed()) {
      const videoDuration = playerGetDuration(playerMgr);
      let videoPos = calculateVodPos(videoDuration, progress);
      if (formatSecondsToTimeDisplay(videoPos) > elapsedTime) {
        handleOnDemandProgressFastForward(
          progress,
          videoPos,
          isFFEnabled,
          setProgressStyle,
          updateVodProgress,
          seekPlayback,
          trackConvivaAppTrackerEvent,
          getMediaNodeMetaData,
          onPlaybackStateChange,
          translate,
          videoStarted
        );
      } else {
        handleOnDemandProgressRewind(
          progress,
          videoPos,
          isRWEnabled,
          setProgressStyle,
          updateVodProgress,
          seekPlayback,
          trackConvivaAppTrackerEvent,
          getMediaNodeMetaData,
          onPlaybackStateChange,
          translate,
          videoStarted
        );
      }
    }
    if (isRestartedStream) {
      if (playerIsLive(playerMgr)) {
        playerUpdateLiveRestartProgress(
          playerMgr,
          progress,
          isRestartTrickPlayAllowed,
          displayToastNotification,
          isSafari
        );
      } else if (isVxOA9RestartStream(playerMgr)) {
        // VxOA 9.0 restart manifests load as in-progress, so the bar sends a 0-100 percentage of the program
        playerUpdateLiveRestartProgress(
          playerMgr,
          restartPercentToTimestamp(playerMgr, progress),
          isRestartTrickPlayAllowed,
          displayToastNotification,
          isSafari
        );
      } else {
        playerUpdateLookBackProgress(
          playerMgr,
          progress,
          isRestartTrickPlayAllowed,
          displayToastNotification,
          setProgressStyle,
          seekPlayback
        );
      }
    } else if (playerIsLive(playerMgr)) {
      if (type === LIVE) {
        // Live player logic
        playerUpdateLiveProgress(playerMgr, progress, isSafari);
      }
    }
    signalUserActivity();
  };

  /*
    Seeks forward or backward to the set time
  */
  const seekPlayback = (seekTarget, appendCurrentPos = true) => {
    const { currentPos, videoDuration } = playerGetSeekParameters(playerMgr);
    const absoluteCurrentPos =
      isHlsStream(currentManifestUrl.current) && !isLookbackStream
        ? playerGetCurrentPlaybackTime(playerMgr, currentManifestUrl.current)
        : currentPos;
    let newPos = playerCalculateSeekPosition(absoluteCurrentPos, seekTarget, appendCurrentPos);
    if (newPos >= videoDuration) {
      if (isLookbackAssetSeeking(isRestartedStream, playerMgr, currentPos)) {
        newPos = lookbackSeekableEndPos(currentPos, seekTarget, startTime);
      } else {
        newPos = playerSeekableEndPos(videoDuration);
      }
    }

    if (isDynamicAsset()) {
      if (appProvider.panicMode) {
        handlePanicLiveSeeking(onPlaybackStateChange, translate);
      } else if (isVxOA9RestartStream(playerMgr) && !isHlsStream(currentManifestUrl.current)) {
        // newPos is relative to program start here, so the epoch landTime/now checks below don't apply
        playerVxOA9RestartSeek(playerMgr, newPos, displayToastNotification);
      } else {
        playerHandleDynamicSeeking(
          playerMgr,
          newPos,
          seekTarget,
          landTime,
          appendCurrentPos,
          displayToastNotification,
          isSafari,
          shakaHlsLandTimeSeekHead
        );
      }
      toggleSpinningLoader(false);
    } else {
      playerHandleStaticSeeking(playerMgr, newPos);
    }
  };

  const onSubTitleChange = (event, shouldTrackAnalytics = true) => {
    shakaHandleSubtitleChange(
      isClosedCaptionEnabled,
      playerMgr,
      event,
      signalUserActivity,
      isDynatraceTrackingEnabled,
      shouldTrackAnalytics
    );
  };

  const onADToggleChange = (isADEnabled) => {
    playerHandleADToggleChange(
      isADEnabled,
      isDescribedVideoEnabled,
      playerMgr,
      signalUserActivity,
      describedVideo,
      isDynatraceTrackingEnabled
    );
  };

  const updateLandTime = () => {
    landTime.current = playerCalculateLandTime(pauseLiveProperties);
  };

  const liveProgress = () => {
    let liveProgressPercentage;
    const playbackCurrentTime = shakaGetCurrentTime(playerMgr);
    if (type === LIVE && !isPauseLiveAllowed.current) {
      liveProgressPercentage = calculateNonPltvStreamProgress(startTime, endTime);
    } else if (landTime.current !== null && !isTimestampFromPastDay(playbackCurrentTime)) {
      liveProgressPercentage = isHlsStream(currentManifestUrl.current)
        ? shakaHlsLiveProgress(playerMgr, startTime, endTime, landTime)
        : playerLiveStreamProgress(playerMgr, startTime, endTime, landTime);
    } else if (isRestartedStream && !playerIsLive(playerMgr)) {
      liveProgressPercentage = playerRestartedStreamProgress(
        playerMgr,
        currentPlaybackTime,
        startTime,
        endTime,
        landTime
      );
    } else {
      liveProgressPercentage = isHlsStream(currentManifestUrl.current)
        ? playbackCurrentTime > 0
          ? shakaHlsLiveProgress(playerMgr, startTime, endTime, landTime)
          : 0
        : playerLiveStreamProgress(playerMgr, startTime, endTime, landTime);
    }
    playerToggleLiveProgramUpdate(isFast, liveProgramCanUpdate, playerMgr, endTime, IsSafari(), setTriggerSafariUpdate);
    liveProgressPercentage = playerHandleLiveProgressEnd(
      isPauseLiveAllowed,
      liveProgressPercentage,
      onPlaybackStateChange,
      playerMgr,
      endTime,
      leftMargin,
      landTime,
      startTime,
      isSafari
    );
    if (!Number.isNaN(liveProgressPercentage)) {
      return Math.max(liveProgressPercentage, 0);
    }
    return 0;
  };

  const liveBufferProgress = () => {
    const currentTime = getCurrentTimestamp();
    // In Safari, sometimes there is a premature call to set bufferIndicatorWidth before the landTime is set. This usually happens on first player load. So we need to make sure we don't return liveBufferProgressPercentage when the landTime is not available.
    if (landTime.current === null) {
      return 0;
    }
    // Keep prev end time to lock PLTV progress buffer for ended program. This would prevent PLTV progress buffer to keep extending at the end of a program.
    if (!prevEndTime || currentTime >= prevEndTime.current) {
      prevEndTime.current = endTime;
    }
    let liveBufferProgressPercentage = getLiveBufferPercentage(currentTime, landTime.current, startTime, endTime);
    if (prevEndTime.current === endTime) {
      prevLiveBufferProgressPercentage.current = liveBufferProgressPercentage;
    } else {
      liveBufferProgressPercentage = prevLiveBufferProgressPercentage.current;
    }
    const isCurrentlyAiring = updateLiveProgramAiringStatus(
      currentTime,
      endTime,
      programCurrentlyAiring,
      setProgramCurrentlyAiring
    );
    if (!isCurrentlyAiring) {
      liveBufferProgressPercentage = ((endTime - landTime.current) / (endTime - startTime)) * 100;
    }
    return Math.min(liveBufferProgressPercentage, 100);
  };

  const getEpisodeInfo = (title) => {
    if (!liveProgramDetails.metadata?.genres?.includes("News")) {
      // We don't display second line for news episodes as per TCDWC-2425
      const uaEpisodeObject = getAutoGeneratedObject(liveProgramDetails.metadata);
      const episodeInfo = getAutogeneratedEpisodeString(uaEpisodeObject, liveProgramDetails.metadata);
      return episodeInfo.trim() === title ? null : episodeInfo;
    }
  };

  const recordingCTA = useMemo(() => {
    if (recordingInfo) {
      let isRecordingScheduled, isRecordingConflicted, isSeriesRecordingScheduled, isSeriesRecordingConflicted;
      if (isMR) {
        isRecordingScheduled = recordingInfo.recordingEventScheduledCheck;
        isSeriesRecordingScheduled = recordingInfo.recordingSeriesScheduledCheck;
        isRecordingConflicted = recordingInfo.recordingEventConflictCheck;
        isSeriesRecordingConflicted = recordingInfo.recordingSeriesConflictCheck;
      }
      return getRecordingCTAs(
        isMR,
        recordingInfo,
        liveProgramDetails,
        isRecordingConflicted,
        isRecordingScheduled,
        isSeriesRecordingConflicted,
        isSeriesRecordingScheduled,
        false,
        true
      );
    }

    return null;
  }, [isMR, liveProgramDetails, recordingInfo]);

  const signalUserActivity = useCallback(() => {
    if (restartUserInactivityTimer) {
      restartUserInactivityTimer();
    }
  }, [restartUserInactivityTimer]);

  const getRecordingButton = useCallback(() => {
    const handleClick = (event) => {
      event.stopPropagation();
      signalUserActivity();
      if (isNotAtLivePosition()) {
        displayToastNotification("recordings_restriction_from_live");
      } else {
        recordingButtonClickHandler(recordingInfo);
      }
    };

    return (
      <RecordingCTA
        recordingCTA={recordingCTA}
        onClickHandler={handleClick}
        hideCTA={!recordingCTA || !programCurrentlyAiring}
      />
    );
  }, [
    recordingCTA,
    programCurrentlyAiring,
    signalUserActivity,
    isNotAtLivePosition,
    displayToastNotification,
    recordingButtonClickHandler,
    recordingInfo,
  ]);

  /**
   * Opens the purchase modal using data from liveProgramDetails.
   * This is currently only applicable for PPV.
   */
  const openPurchaseModal = useCallback(() => {
    showPurchaseModal(
      liveProgramDetails,
      purchaseCallback,
      ppvPurchasePackage,
      displayModalPopup,
      getAVSPosterArtImage
    );
  }, [liveProgramDetails, purchaseCallback, ppvPurchasePackage, displayModalPopup]);

  const mouseEventHandler =
    !(showNextEpisodeCard && !isSideNavOpen) && !showStillWatchingPrompt ? videoControlsMouseMove : undefined;

  // For user specific recording playback, we use its recording portion start time, for all other playbacks, we use program start time.
  const startTimeEpoch = startTime;

  // Duration variable
  let duration = isDynamicAsset() ? endTime / 1000 : playerGetDuration(playerMgr);

  const jumpToLivePoint = () => {
    if (isRestartedStream) {
      handleRestartedJumpToLive(handleAVSStopContent, landTime, switchLive);
    } else {
      playerHandleJumpToLive(hasJumpedToLive, playerMgr, setPlayPauseIcon);
    }
  };

  const shouldDisplayForwardButton = () => {
    if (isPlayerControlsDisable || isFast || (checkIfCatchUpAllowed() && !isFFEnabled) || !isFFEnabled) {
      return false;
    }
    return true;
  };

  const shouldDisplayRewindButton = () => {
    if (isPlayerControlsDisable) return false;
    if (checkIfPauseLiveAllowed()) return true;
  };

  const shouldDisplayRestartButton = () => {
    if (isPlayerControlsDisable) {
      // do not display the restart icon for live player in panic mode, or if on-demand player's stream is not started
      return false;
    }
    if (
      isLiveRestartButtonVisible(
        type,
        isRestartedStream,
        checkIfStartOverAllowed(),
        programCurrentlyAiring,
        checkIfCatchUpAllowed()
      )
    ) {
      return true;
    }
    return false;
  };

  const shouldHideSidebarToggleButton = () => {
    if (hideListChannel || errCode?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS)) {
      return true;
    }
    return false;
  };

  return (
    <>
      <PlayerWrapper
        videoWrapperRef={videoWrapper}
        mouseEventHandler={mouseEventHandler}
        videoControlsKeyUp={videoControlsKeyUp}
        handleVolume={handleVolume}
        playerMgr={playerMgr}
        onClickToggleState={onClickToggleState}
      >
        {showErrorOverlay && <PlaybackError isSideNavOpen={isSideNavOpen} />}
        {isFullScreen(document) && toastData && Object.values(toastData).some((value) => value !== null) && (
          <ToastNotification /> // rendering inside browser's top layer when app is in full screen mode
        )}
        <BackgroundImage showBgImage={showBgImage} bgImageSrc={bgImage} videoClassName={videoClassName} />
        {videoSource && (
          <ShakaPlayerContainer>
            <VideoPlayer
              ref={playerMgr}
              source={videoSource}
              width={1920}
              height={1080}
              playerConfig={{
                events: {
                  [VideoPlayerEvent.SOURCE_LOADING]: handleSourceLoadingEvent,
                  [VideoPlayerEvent.SOURCE_LOADED]: handleSourceLoadedEvent,
                  [VideoPlayerEvent.READY]: handleReadyEvent,
                  [VideoPlayerEvent.PLAYING]: handlePlayingEvent,
                  [VideoPlayerEvent.PAUSED]: handlePausedEvent,
                  [VideoPlayerEvent.PLAYBACK_FINISHED]: isRestartedStream ? null : handlePlaybackFinishedEvent,
                  [VideoPlayerEvent.WARNING]: handleWarningEvent,
                  [VideoPlayerEvent.ERROR]: handlePlaybackError,
                  [VideoPlayerEvent.CAPTION_TRACK_ADDED]: handleCaptionTrackAddedEvent,
                  [VideoPlayerEvent.TIME_CHANGED]: handleTimeChangedEvent,
                  [VideoPlayerEvent.SEEKING]: handleSeekingEvent,
                  [VideoPlayerEvent.SEEKED]: handleSeekedEvent,
                  [VideoPlayerEvent.BUFFERING]: handleBufferingEvent,
                  [VideoPlayerEvent.BUFFERED]: handleBufferedEvent,
                  [VideoPlayerEvent.VIDEO_QUALITY_CHANGED]: handleVideoQualityChanged,
                },
                autoplay: true,
                // Buffer & latency: tuned for live with 30s live edge target
                bufferBehind: 30, // seconds of backward buffer to retain
                bufferingGoal: 20, // seconds ahead to buffer
                rebufferingGoal: 2, // seconds needed in buffer before resuming — low for fast start
                defaultPresentationDelay: 30, // seconds behind the live edge to start playback
                ignoreSuggestedPresentationDelay: true, // override manifest-suggested delay to use our own
                maxDisabledTime: 30, // re-enable failed variants after 30s instead of permanently disabling
                // ABR: downgrade early to preserve buffer headroom
                switchInterval: 5, // minimum seconds between ABR quality switches
                bandwidthDowngradeTarget: 0.9, // downgrade quality when bandwidth drops to 90% of current requirement
                bandwidthUpgradeTarget: 0.7, // upgrade quality when bandwidth reaches 70% of next level
                // Retry: survive transient CDN/network errors
                retryParameters: { maxAttempts: 5 }, // more retries before failing a segment request
                // Live sync: keeps playback within a target window behind the live edge
                liveSyncEnabled: true,
                liveSyncTargetLatency: 30, // target seconds behind live edge
                liveSyncTargetLatencyTolerance: 8, // acceptable drift (±8s) before playback rate adjusts
                liveSyncMaxPlaybackRate: 1.05, // speed up to 1.05x to catch up to live edge
                liveSyncMinPlaybackRate: 0.95, // slow down to 0.95x if too close to live edge
                liveSyncPanicMode: true, // seek to live edge if latency exceeds panic threshold
                liveSyncPanicThreshold: 60, // seconds behind live edge before a panic seek occurs
                //preferredAudioCodecs: ["mp4a.40.2", "ac-3"], // Allows us to override the audio codec if passed in
              }}
            />
          </ShakaPlayerContainer>
        )}

        <GradientBackground
          showNextEpisodeCard={showNextEpisodeCard}
          isSideNavOpen={isSideNavOpen}
          showStillWatchingPrompt={showStillWatchingPrompt}
          showBgImage={showBgImage}
          videoControlsStyle={videoControlsStyle}
        />

        <ClosedCaption ccList={ccList} isSideNavOpen={isSideNavOpen} />

        <StillWatchingCloseIcon
          showNextEpisodeCard={showNextEpisodeCard}
          isSideNavOpen={isSideNavOpen}
          showStillWatchingPrompt={showStillWatchingPrompt}
          altText={translate("close")}
          closeClickHandler={closeClickHandler}
        />

        {(!showNextEpisodeCard || isSideNavOpen) && (
          <div className={videoControlsName} ref={videoCCContainerRef} style={videoControlsStyle}>
            <div className="playbackControl-top dis-flex">
              <div className="dis-flex align-right">
                <FullScreenToggle
                  isFullScreen={isFullScreen(Document)}
                  translate={translate}
                  onFullScreenButtonHandler={onFullScreenButtonHandler}
                />
                <SidebarToggle
                  isFullScreen={isFullScreen(Document)}
                  hideListChannel={shouldHideSidebarToggleButton()}
                  isOnDemandType={false}
                  showListButton={showListButton}
                  translate={translate}
                  isRecording={false}
                  onClickHandler={() => handleListIconClick(telusConvivaAnalytics.onSwitchProgram)}
                />
              </div>
              <CloseButton translate={translate} closeClickHandler={closeClickHandler} />
            </div>
            <div className="playbackControl-metaData dis-flex">
              <div className="metadata">
                <div
                  className={
                    image ? "info dis-flex align-item-start justify-item-start" : "info dis-flex justify-item-start"
                  }
                >
                  <ChannelInfo
                    channelNumber={channelNumber}
                    type={type}
                    image={image}
                    onError={(e) => handleImageError(e, defaultChannelLogoIcon)}
                  />
                  <Details
                    title={title}
                    subTitle={subTitle}
                    episodeInfo={type === LIVE && liveProgramDetails ? getEpisodeInfo(title) : null}
                  />
                  <div className="dis-flex align-right pos-rel">
                    <VolumeControl
                      volumeRef={volumeRef}
                      onMouseEnter={() => {
                        if (userHasClick) volumeButtonHoverHandler();
                      }}
                      onMouseLeave={() => {
                        setVolumeVisibility(false);
                      }}
                      isMuted={isMuted}
                      volumeLevel={volumeLevel}
                      translate={translate}
                      volumeButtonRef={volumeButtonRef}
                      volumeButtonHandler={volumeButtonHandler}
                      volumeVisibility={volumeVisibility}
                      playerMgr={playerMgr}
                      onVolumeSlideChange={onVolumeSlideChange}
                      volumeBarRef={volumeBarRef}
                    />
                    <SettingsControl
                      settingsRef={settingsRef}
                      isCCAvailable={isCCAvailable}
                      isADAvailable={isADAvailable}
                      translate={translate}
                      settingButtonHandler={settingButtonHandler}
                      spinnerToggle={spinnerToggle}
                      settingsDropdownVisibility={settingsDropdownVisibility}
                      isClosedCaptionEnabled={isClosedCaptionEnabled}
                      onSubTitleChange={onSubTitleChange}
                      onADToggleChange={onADToggleChange}
                      isDescribedVideoEnabled={isDescribedVideoEnabled}
                      playerMgr={playerMgr}
                    />
                  </div>
                </div>
                <div className="progressBar-wrap">
                  <ProgressBar
                    progress={checkIfCatchUpAllowed() ? progressStyle : liveProgress()}
                    bufferProgress={
                      isPauseLiveAllowed.current && !isLookbackStream && showLiveProgress.current
                        ? liveBufferProgress()
                        : 0
                    }
                    onProgressClick={setProgress}
                    disabled={spinnerToggle}
                    playerMgr={playerMgr.current}
                    playerType={type}
                    elapsedTime={!Number.isNaN(elapsedTime) ? elapsedTime : ""}
                    duration={duration}
                    leftMargin={leftMargin.current}
                    showLiveProgress={showLiveProgress.current}
                    currentPlaybackTime={currentPlaybackTime}
                    endTime={endTime}
                    startTime={startTime}
                    updateLandTime={updateLandTime}
                    isPauseLiveAllowed={isPauseLiveAllowed.current}
                    pauseLiveProperties={pauseLiveProperties}
                    isOnDemandType={false}
                    checkIfPauseLiveAllowed={checkIfPauseLiveAllowed}
                    isRestartedStream={isRestartedStream}
                    isLookbackStream={isLookbackStream}
                    isTimeShifting={isTimeShifting.current}
                    isSeeking={isSeeking.current}
                    isRestartTrickPlayAllowed={isRestartTrickPlayAllowed.current}
                    isPlayerControlsDisable={isPlayerControlsDisable}
                    startTimeEpoch={startTimeEpoch}
                    remainingTime={remainingTime}
                    userRecordingStopTime={null}
                    isRecording={false}
                    currentImageTrack={currentImageTrack}
                    isZoetropeEnabled={isLiveZoetropeEnabled}
                  />
                </div>
              </div>
            </div>
            <div className="playbackControl-bottom dis-flex">
              <React.Fragment>
                <div className="dis-flex">
                  <RewindButton
                    shouldDisplayRewindButton={shouldDisplayRewindButton()}
                    translate={translate}
                    rewindButtonHandler={rewindButtonHandler}
                    spinnerToggle={spinnerToggle}
                  />
                  <PlayPauseButton
                    shouldDisplayPlayPauseButton={!isPlayerControlsDisable && checkIfPauseLiveAllowed()}
                    playPauseIcon={playPauseIcon}
                    translate={translate}
                    playPauseButtonHandler={playPauseButtonHandler}
                    playButtonRef={playButtonRef}
                    spinnerToggle={spinnerToggle}
                  />
                  <ForwardButton
                    shouldDisplayForwardButton={shouldDisplayForwardButton()}
                    isVOD={false}
                    translate={translate}
                    forwardButtonHandler={forwardButtonHandler}
                    spinnerToggle={spinnerToggle}
                  />
                </div>
                <div className="dis-flex align-right">
                  <RestartButton
                    shouldDisplayRestartButton={shouldDisplayRestartButton()}
                    translate={translate}
                    onClickHandler={() => startOverStream(false)}
                    spinnerToggle={spinnerToggle}
                  />
                  <JumpToLiveButton
                    shouldDisplayJumpToLive={
                      !isPlayerControlsDisable &&
                      !isFast &&
                      (playerIsLive(playerMgr) || isRestartedStream) &&
                      type === LIVE &&
                      isNotAtLivePosition()
                    }
                    translate={translate}
                    jumpToLivePoint={jumpToLivePoint}
                    spinnerToggle={spinnerToggle}
                  />
                  <RecordingButton
                    isPlayerControlsDisable={isPlayerControlsDisable}
                    getRecordingButton={getRecordingButton}
                    showRecordingCTALoading={showRecordingCTALoading}
                  />
                  <PurchaseButton
                    showPurchaseCTA={showPurchaseCTA}
                    altText={translate("order_for_stb").replace(
                      "%s",
                      getFormattedPrice(getPackagePrice(ppvPurchasePackage), appLanguage) ?? ""
                    )}
                    openPurchaseModal={openPurchaseModal}
                    spinnerToggle={spinnerToggle}
                  />
                </div>
              </React.Fragment>
            </div>
          </div>
        )}

        {contentLockedOverlay}

        {showStillWatchingPrompt && (
          <StillWatchingCard
            ref={stillWatchingCard}
            className={`still-watching ${isSideNavOpen ? "shrink" : ""}`}
            onButtonClick={() => {
              signalUserActivity();
              trackGenericAction(
                ANALYTICS_EVENT_TYPES.STILL_WATCHING,
                getMediaNodeMetaData(),
                isDynatraceTrackingEnabled
              );
            }}
            onCountdownComplete={() => closeClickHandler(null, true)}
          />
        )}
      </PlayerWrapper>
    </>
  );
}

LivePlayer.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string.isRequired,
  streamInfo: PropTypes.shape({
    streamMode: PropTypes.string,
    manifestURL: PropTypes.string,
    playbackType: PropTypes.string,
    drmType: PropTypes.string,
    LA_URL: PropTypes.string,
    LA_CERT_URL: PropTypes.string,
    authToken: PropTypes.string,
  }),
  metadataInfo: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    image: PropTypes.string,
    duration: PropTypes.number,
    startTime: PropTypes.object,
  }),
  control: PropTypes.string,
  onPlaybackStateChange: PropTypes.func,
  nextProgramInfo: PropTypes.shape({
    nextProgram: PropTypes.object,
    nextEpisodePoster: PropTypes.string,
    loadNextProgram: PropTypes.func,
  }),
  showStillWatchingPrompt: PropTypes.bool,
  restartUserInactivityTimer: PropTypes.func,
  updateVodProgress: PropTypes.func,
  isReadyCallback: PropTypes.func,
  setIsReadyToChangeStreams: PropTypes.func,
  contentLockedOverlay: PropTypes.element,
};

LivePlayer.defaultProps = {
  className: "",
  nextProgramInfo: {},
  updateVodProgress: () => null,
};

export default LivePlayer;
